/**
 * Trust frameworks list hook — fetches real `framework_metadata` joined
 * with per-tenant `framework_tenant_state` from `/api/v1/frameworks-list`
 * and adapts each row into the mock UI's `Framework` VM shape so the
 * existing pixel-perfect /trust and /trust/frameworks pages render
 * without JSX changes.
 *
 * This replaces the hardcoded `FRAMEWORKS` array from
 * `lib/mock-trust-data.ts`.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  Framework,
  FrameworkId,
  FrameworkStatus,
  FrameworkCategory,
} from './mock-trust-data';

export interface ServerFramework {
  id: string;
  shortName: string;
  fullName: string;
  description: string;
  category: string;
  authority: string;
  jurisdiction: string;
  controlCount: number;
  retentionYears: number;
  updateFrequency: string;
  enforcementDate: string | null;
  iconColor: string;
  licensed: boolean;
  enabled: boolean;
  status: string;
  setupProgress: number;
  setupCompleted: boolean;
  controlsPassing: number;
  controlsFailing: number;
  controlsNotApplicable: number;
  controlsImplemented: number;
  evidenceCount: number;
  evidenceFresh: number;
  evidenceStale: number;
  lastAssessmentDate: string | null;
  nextAssessmentDate: string | null;
  certificationExpiry: string | null;
  assessor: string | null;
  ownerUserId: string | null;
  openFindings: number;
  recentChanges: number;
  riskRating: string;
  activatedAt: string | null;
}

interface ListResponse {
  frameworks: ServerFramework[];
}

// hyphenated server id → underscored mock FrameworkId enum
const ID_MAP: Record<string, FrameworkId> = {
  'cmmc-l2': 'cmmc_l2',
  'eu-ai-act': 'iso42001',
  'nist-ai-rmf': 'nist_ai_rmf',
  fedramp_moderate: 'fedramp_moderate',
};

export function adaptFramework(s: ServerFramework): Framework {
  return {
    id: ID_MAP[s.id] ?? (s.id as FrameworkId),
    name: s.shortName,
    shortName: s.shortName,
    fullName: s.fullName,
    description: s.description,
    category: s.category as FrameworkCategory,
    authority: s.authority,
    jurisdiction: s.jurisdiction,
    controlCount: s.controlCount,
    status: s.status as FrameworkStatus,
    licensed: s.licensed,
    enabled: s.enabled,
    activatedAt: s.activatedAt,
    activatedByUserId: null,
    setupCompleted: s.setupCompleted,
    setupAnswers: {},
    setupProgress: s.setupProgress,
    controlsImplemented: s.controlsImplemented,
    controlsPassing: s.controlsPassing,
    controlsFailing: s.controlsFailing,
    controlsNotApplicable: s.controlsNotApplicable,
    evidenceCount: s.evidenceCount,
    evidenceFresh: s.evidenceFresh,
    evidenceStale: s.evidenceStale,
    lastAssessmentDate: s.lastAssessmentDate,
    nextAssessmentDate: s.nextAssessmentDate,
    certificationExpiry: s.certificationExpiry,
    assessor: s.assessor,
    ownerUserId: s.ownerUserId,
    retentionYears: s.retentionYears,
    updateFrequency: s.updateFrequency as Framework['updateFrequency'],
    enforcementDate: s.enforcementDate,
    iconColor: s.iconColor,
    recentChanges: s.recentChanges,
    openFindings: s.openFindings,
    riskRating: s.riskRating as Framework['riskRating'],
  };
}

export function useTrustFrameworks() {
  return useQuery<ListResponse, Error, Framework[]>({
    queryKey: ['frameworks-list'],
    queryFn: () => apiClient.get<ListResponse>('/frameworks-list'),
    select: (r) => (r.frameworks ?? []).map(adaptFramework),
    staleTime: 60_000,
  });
}
